/**
 * Fijnmazig rechtenmodel (RBAC) voor het admin-dashboard. Eén bron van waarheid voor
 * zowel de client (knoppen/secties tonen) als de Cloud Functions + Firestore-rules
 * (autoritatieve handhaving). Rechten zitten in de Firebase custom claims:
 *   - `admin: true`  → eigenaar/superuser: alle rechten (vangnet, nooit buitensluiten)
 *   - `perms: string[]` → losse rechten voor een medewerker
 *
 * UI verbergt alleen; de server beslist. Voeg een recht hier toe en het werkt overal.
 */
export const ADMIN_PERMISSIONS = [
  "orders", // bestellingen inzien + status bijwerken
  "returns", // retouren/herroeping afhandelen
  "customers", // klantgegevens + bestellingen inzien
  "discounts", // klantkortingen toekennen (raakt prijs/marge)
  "settings", // verzendkosten, marges, levertijden
  "catalog", // producten/catalogus-overrides + Timmers-sync
  "promotions", // promoties, homepage & acties
  "roles", // rollen/rechten van medewerkers beheren
  "finance", // inkoopfacturen van leveranciers + betaalruns
] as const;

export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number];

/** Claims-vorm zoals die in de Firebase-token staat (client + functions delen dit). */
export interface AdminClaims {
  admin?: boolean;
  perms?: string[];
}

/** Heeft deze claim-set het gevraagde recht? `admin:true` impliceert alles. */
export function hasPermission(claims: AdminClaims | null | undefined, perm: AdminPermission): boolean {
  if (!claims) return false;
  if (claims.admin === true) return true;
  return Array.isArray(claims.perms) && claims.perms.includes(perm);
}

/** Heeft deze claim-set überhaupt admin-toegang (eigenaar of ≥1 recht)? */
export function hasAnyAdminAccess(claims: AdminClaims | null | undefined): boolean {
  if (!claims) return false;
  return claims.admin === true || (Array.isArray(claims.perms) && claims.perms.length > 0);
}
